import { useState, type ReactElement, type ReactNode } from 'react';
import styled from 'styled-components';
import colors from 'styles/colors';
import sizes from 'styles/sizes';
import borders from 'styles/borders';
import CheckCircle from '@mui/icons-material/CheckCircle';
import type { ToastProps } from 'common/ui/Toaster/Toast';
import type { GenericSnackBarContentProps } from 'common/GenericSnackBar/GenericSnackBar';
import { HP_TOAST_DURATION } from './hpStepToastMessages';
import { StepToastCountdown } from './StepToastCountdown';

const ToastContainer = styled.div`
  display: flex;
  align-items: center;
  background-color: ${colors.gray5};
  border-radius: ${borders.borderRadius.medium};
  padding: 8px 16px;
  box-shadow:
    0px 3px 5px -1px rgba(0, 0, 0, 0.2),
    0px 6px 10px 0px rgba(0, 0, 0, 0.14),
    0px 1px 18px 0px rgba(0, 0, 0, 0.12);
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
    color: ${colors.green6};
  }
`;

const Message = styled.span`
  flex: 1;
  font-size: ${sizes.textBody1};
  line-height: 18px;
  color: ${colors.gray10};
  padding: 8px 0 8px 8px;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  border-radius: 8px;
  padding: 4px 12px;
  font-size: ${sizes.textBody1};
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.25px;
  color: #86b8fb;
  cursor: pointer;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background-color: rgba(134, 184, 251, 0.08);
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

export interface StepToastProps {
  /** The message to display */
  message: ReactNode;
  /** Label for the action button (e.g., "UNDO") */
  actionLabel?: string;
  /** Called when the action button is clicked */
  onAction?: () => void;
  /** Custom icon to display (defaults to CheckCircle) */
  icon?: ReactNode;
  /** Disables the action button (e.g., after countdown completes) */
  actionDisabled?: boolean;
}

export const StepToast = ({
  message,
  actionLabel,
  onAction,
  icon,
  actionDisabled,
}: StepToastProps): ReactElement => {
  return (
    <ToastContainer data-testid="step-toast">
      <IconWrapper>{icon ?? <CheckCircle />}</IconWrapper>
      <Message data-testid="step-toast-message">{message}</Message>
      {actionLabel && (
        <ActionButton
          type="button"
          onClick={onAction}
          disabled={actionDisabled || !onAction}
          data-testid="step-toast-action"
        >
          {actionLabel}
        </ActionButton>
      )}
    </ToastContainer>
  );
};

/**
 * Creates a ContentComponent for use with the Toast system's ContentComponent prop.
 * The returned component receives standard toast props but renders the custom StepToast layout.
 *
 * @example
 * const { enqueueToast } = useToasterDispatch();
 *
 * enqueueToast('This step has been updated with your changes', {
 *   ...getDefaultHPToastOptions(),
 *   ContentComponent: createStepToastContentComponent({
 *     actionLabel: 'UNDO',
 *     onAction: () => handleUndo(),
 *   }),
 * });
 */
export const createStepToastContentComponent = (
  stepProps: Omit<StepToastProps, 'message'> & {
    /** When set, renders a countdown circle as the icon instead of the default. */
    countdownDuration?: number;
  }
): React.ComponentType<GenericSnackBarContentProps> => {
  const { countdownDuration, ...restProps } = stepProps;
  const Component = ({ message, onClose }: GenericSnackBarContentProps) => {
    const [countdownDone, setCountdownDone] = useState(false);

    return (
      <StepToast
        message={message}
        {...restProps}
        icon={
          countdownDuration != null ? (
            <StepToastCountdown
              duration={countdownDuration}
              onComplete={() => setCountdownDone(true)}
            />
          ) : (
            restProps.icon
          )
        }
        actionDisabled={countdownDone}
        onAction={
          restProps.onAction
            ? () => {
                restProps.onAction?.();
                // Toast infra wraps onClose as () => onClose?.(toastKey), so
                // the event/reason params are unused — pass synthetic values.
                onClose?.({} as React.SyntheticEvent, 'escapeKeyDown');
              }
            : undefined
        }
      />
    );
  };
  Component.displayName = 'StepToastContent';
  return Component;
};

/**
 * Returns the default toast options for HP step toasts.
 * Use this to ensure consistent behavior across all HP-related toasts.
 *
 * @param overrides - Optional overrides for specific toast options
 */
export const getDefaultHPToastOptions = (overrides?: Partial<ToastProps>): Partial<ToastProps> => ({
  position: 'above-step-bar',
  autoHideDuration: HP_TOAST_DURATION,
  // HP step toasts must always be visible regardless of user-configured
  // notification severity filters. Using 'error' ensures they are never hidden.
  severity: 'error',
  ...overrides,
});
